import { useCallback, useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getMyAudience, saveAudience } from "@/lib/intel/feeds.functions";
import { getWorkspace, normalizeWorkspace, type WorkspaceId, type WorkspaceProfile } from "./workspaces";

const LS_KEY = "cario:workspace";

function readCache(): WorkspaceId {
  if (typeof window === "undefined") return "other";
  try {
    return normalizeWorkspace(window.localStorage.getItem(LS_KEY));
  } catch {
    return "other";
  }
}

/**
 * Resolves the signed-in user's workspace profile. Renders instantly from a
 * local cache, then reconciles with the value stored on the profile row.
 */
export function useWorkspace(): {
  id: WorkspaceId;
  workspace: WorkspaceProfile;
  setWorkspace: (id: WorkspaceId) => Promise<void>;
  saving: boolean;
  loading: boolean;
} {
  const fetchAudience = useServerFn(getMyAudience);
  const persist = useServerFn(saveAudience);
  const qc = useQueryClient();
  const [local, setLocal] = useState<WorkspaceId>("other");
  const [saving, setSaving] = useState(false);

  useEffect(() => setLocal(readCache()), []);

  const q = useQuery({
    queryKey: ["my-workspace"],
    queryFn: () => fetchAudience({}),
    staleTime: 5 * 60 * 1000,
  });

  const remote = q.data?.audience ? normalizeWorkspace(q.data.audience) : null;

  useEffect(() => {
    if (!remote) return;
    setLocal(remote);
    try { window.localStorage.setItem(LS_KEY, remote); } catch { /* ignore */ }
  }, [remote]);

  const setWorkspace = useCallback(async (id: WorkspaceId) => {
    setLocal(id);
    try { window.localStorage.setItem(LS_KEY, id); } catch { /* ignore */ }
    setSaving(true);
    try {
      await persist({ data: { audience: id } });
      await qc.invalidateQueries({ queryKey: ["my-workspace"] });
    } finally {
      setSaving(false);
    }
  }, [persist, qc]);

  const id = remote ?? local;
  return { id, workspace: getWorkspace(id), setWorkspace, saving, loading: q.isLoading };
}